import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { CommandEntity } from './command-items';

type EntitiesState =
  | { status: 'idle'; entities: readonly CommandEntity[] }
  | { status: 'loading'; entities: readonly CommandEntity[] }
  | { status: 'ready'; entities: readonly CommandEntity[] };

/**
 * "Find entities" — the second half of the standard's palette, over the one entity the platform
 * has that a person actually looks up by name: the Clinical Study.
 *
 * WHY NOT A CROSS-MODULE SEARCH ENDPOINT. There is none, and adding one is a bigger decision than
 * this: it would have to span four independently deployed services, decide a relevance order across
 * them, and re-derive per-module authorization for every hit. What exists already is a list route
 * whose server-side confinement is exactly right — `GET /v1/clinical-study/studies` returns "the
 * Studies this caller may read: every one of the operating Organization for a study administrator
 * or CRO monitor, and only the assigned ones otherwise". Searching that list client-side inherits
 * that confinement for free and cannot widen it.
 *
 * FETCHED ON OPEN, ONCE PER ORGANIZATION. Not on every keystroke, which would be a request per
 * character against a list small enough to filter in memory; and not at shell mount, which would
 * make every page load pay for a feature most loads never use. Switching organization discards the
 * cache, because a study list is scoped to one.
 *
 * SILENT ON FAILURE, like the navigation counter: a caller with no clinical-study role gets a 403,
 * and the honest palette then simply has no study section. An error row would put a failure notice
 * in front of someone who was only trying to reach a settings screen.
 */
export function useCommandEntities(
  open: boolean,
  organizationId: string | undefined,
): { readonly loading: boolean; readonly entities: readonly CommandEntity[] } {
  const [state, setState] = useState<EntitiesState>({ status: 'idle', entities: [] });
  // Which organization the cache above belongs to. `null` means "nothing cached yet".
  const [cachedFor, setCachedFor] = useState<string | null>(null);

  useEffect(() => {
    if (organizationId === undefined) {
      setState({ status: 'idle', entities: [] });
      setCachedFor(null);
      return;
    }
    if (!open || cachedFor === organizationId) {
      return;
    }

    let active = true;
    setState((current) => ({ status: 'loading', entities: current.entities }));

    apiClient
      .GET('/v1/clinical-study/studies')
      .then(({ data, error, response }) => {
        if (!active) {
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          // Marked as cached even so: a caller without the permission would otherwise re-request
          // and re-fail on every single open of the palette.
          setState({ status: 'ready', entities: [] });
          setCachedFor(organizationId);
          return;
        }
        setState({
          status: 'ready',
          entities: data.map((study) => ({
            id: `study:${study.id}`,
            label: study.name,
            path: `/clinical-study/studies/${study.id}`,
          })),
        });
        setCachedFor(organizationId);
      })
      .catch(() => {
        if (active) {
          setState({ status: 'ready', entities: [] });
          setCachedFor(organizationId);
        }
      });

    return () => {
      active = false;
    };
  }, [open, organizationId, cachedFor]);

  return { loading: state.status === 'loading', entities: state.entities };
}
